import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import type { ApprovalRow } from '../lib/overview';
import type { PendingDecision } from '../lib/pr-detail';
import type { ScopeVerdict } from '../lib/scope-gate';
import { absolute, since } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// Pending actions (canvas APP artboard): the account-wide queue of PRs parked on
// `ask_before_acting`, longest-waiting first, as CATEGORY · TARGET · DRAFTED
// ACTION · WAITING · DECISION. Presenter — Home's preview and the `/pending`
// route both render it; each container owns its own decision write.
//
// Every row is a MERGE decision: `/v1/approvals` serves only PRs whose
// `merge_decision` is `ask`, so the category is stated, not guessed. The row
// carries WHY it is back (#5671: `merge_ask_cause`, the domain vocabulary);
// only a re-ask after the approved head moved (`stale_approval`) renders a
// note — the other causes are the queue's ordinary arrivals, saying so again
// on every row would be noise. The drafted action also names the PR and the
// facts the wire carries (author, CI).
//
// APPROVE and SKIP are both `outline`: a row decision is never the view's main
// action, and there is no bulk endpoint to be one. Their ACCESSIBLE names carry
// the ref (`approvals.approve_named`) — a column of buttons all named "Approve"
// tells a screen-reader user nothing about which PR they are merging.
//
// A decision is OF the head the row showed (plan 101 M2): `onDecide` hands it
// back, and a row whose head is not known yet holds both buttons and says why.
//
// A caller who may not decide (`mayDecide: 'denied'`, `write:prs` missing from
// `/v1/me`, #7426) gets no buttons and ONE line above the table saying why —
// never a button whose only answer is a 403. Unsettled holds the buttons.

/** "owner/repo#294" — the decision's target, as the audit trail spells it. */
function ref(row: ApprovalRow): string {
  return `${row.repo_full_name}#${row.github_number}`;
}

/** "dependabot · CI passing" — drops the author when unknown. */
function facts(row: ApprovalRow): string {
  return [row.author_login, t('pending.ci', { status: row.ci_status })]
    .filter((p): p is string => p !== null)
    .join(' · ');
}

export type ApprovalsListProps = {
  readonly rows: readonly ApprovalRow[];
  /** A decision is in flight: every row's controls hold until the list re-reads. */
  readonly busy: boolean;
  /** `write:prs` per `/v1/me` — `denied` drops the buttons for one stated reason. */
  readonly mayDecide: ScopeVerdict;
  /** `headSha` is the head this row SHOWED — the decision is of it (plan 101 M2). */
  readonly onDecide: (prId: string, decision: PendingDecision, headSha: string) => void;
};

export function ApprovalsList(props: ApprovalsListProps): JSX.Element {
  // A row with no known head holds its buttons (above); this is the lock, not
  // the rendering — a disabled attribute is not one (`lib/mutation.ts`).
  const decide = (row: ApprovalRow, decision: PendingDecision): void => {
    if (row.head_sha !== null) props.onDecide(row.id, decision, row.head_sha);
  };
  const columns: readonly DataTableColumn<ApprovalRow>[] = [
    {
      key: 'category',
      header: t('pending.col_category'),
      mono: true,
      class: 'pending-actions__category',
      cell: () => t('pending.category_merge'),
    },
    {
      key: 'target',
      header: t('pending.col_target'),
      mono: true,
      rowHeader: true,
      cell: (row) => (
        <A
          class="pending-actions__target"
          href={orgHref(`/repos/${encodeURIComponent(row.repo_full_name)}/pending`)}
        >
          {ref(row)}
        </A>
      ),
    },
    {
      key: 'action',
      header: t('pending.col_action'),
      cell: (row) => (
        <>
          <span class="pending-actions__title">
            {row.title ?? t('overview.approval_title', { number: row.github_number })}
          </span>
          <Show when={facts(row)}>
            <span class="pending-actions__facts">{facts(row)}</span>
          </Show>
          {/* #5671: the row is back because the approved head moved since —
              the one cause a human must re-review before deciding. */}
          <Show when={row.merge_ask_cause === 'stale_approval'}>
            <span class="pending-actions__hint">{t('approvals.stale_commits')}</span>
          </Show>
        </>
      ),
    },
    {
      key: 'waiting',
      header: t('pending.col_waiting'),
      mono: true,
      align: 'end',
      class: 'pending-actions__waiting',
      cell: (row) => (
        <time datetime={row.opened_iso} title={absolute(row.opened_iso)}>
          {since(row.opened_iso)}
        </time>
      ),
    },
    {
      key: 'decision',
      header: t('pending.col_decision'),
      align: 'end',
      cell: (row) => (
        <Show when={props.mayDecide !== 'denied'}>
          <div class="data-table__actions">
            <Button
              variant="outline"
              size="sm"
              aria-label={t('approvals.approve_named', { ref: ref(row) })}
              disabled={props.busy || props.mayDecide !== 'allowed' || row.head_sha === null}
              onClick={() => decide(row, 'approve')}
            >
              {t('approvals.approve')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('approvals.skip_named', { ref: ref(row) })}
              disabled={props.busy || props.mayDecide !== 'allowed' || row.head_sha === null}
              onClick={() => decide(row, 'skip')}
            >
              {t('approvals.skip')}
            </Button>
            <Show when={row.head_sha === null}>
              <span class="pending-actions__hint">{t('approvals.head_unknown')}</span>
            </Show>
          </div>
        </Show>
      ),
    },
  ];
  return (
    <>
      <Show when={props.mayDecide === 'denied'}>
        <p class="pending-actions__hint" role="status">
          {t('approvals.decide_denied')}
        </p>
      </Show>
      <DataTable
        label={t('pending.table_label')}
        class="pending-actions"
        columns={columns}
        rows={props.rows}
        rowKey={(row) => row.id}
      />
    </>
  );
}
